import * as React from 'react';
import { ComponentType, lazy, ReactNode } from 'react';

export type DynamicComposedComponentOptions = {
	fallback?: ComponentType;
	errorBoundary?: boolean;
	errorComponent?: ComponentType<{ error: Error; retry: () => void }>;
};

export type DynamicComposedComponentProps<T> = {
	children: (Component: T) => ReactNode;
	componentProps?: Record<string, any>;
};

export const dynamicComposedComponent = <
	T extends ComponentType<any> | Record<string, ComponentType<any>> | object
>(
	factory: () => Promise<{ default: T } | Record<string, T>>,
	options: DynamicComposedComponentOptions = {}
) => {
	const { errorBoundary = false, errorComponent: ErrorComponent } = options;

	return lazy(async () => {
		try {
			const module = await factory();

			if (!module || typeof module !== 'object') {
				throw new Error(
					`Failed to load module: module is not a valid object`
				);
			}

			let Component: T;

			if ('default' in module && module.default) {
				Component = module.default as T;
			} else {
				const exports = Object.keys(module);
				if (exports.length === 0) {
					throw new Error('No exports found in module');
				}
				const firstExport = module[
					exports[0] as keyof typeof module
				] as T;
				Component = firstExport;
			}

			const isSingleComponent =
				Component && typeof Component === 'function';
			const isCompoundComponent =
				Component &&
				typeof Component === 'object' &&
				Component !== null;

			if (!isSingleComponent && !isCompoundComponent) {
				throw new Error(
					`No valid React component found in module. Available exports: ${Object.keys(
						module
					).join(', ')}`
				);
			}

			return {
				default: ({ children }: DynamicComposedComponentProps<T>) => {
					return children(Component as T);
				},
			};
		} catch (error) {
			console.error(
				`Dynamically imported Composed Component error:`,
				error
			);

			if (errorBoundary && ErrorComponent) {
				return {
					default: () => {
						const ErrorComponentToRender =
							ErrorComponent as ComponentType<{
								error: Error;
								retry: () => void;
							}>;
						return React.createElement(ErrorComponentToRender, {
							error: error as Error,
							retry: () => window.location.reload(),
						});
					},
				};
			}

			throw error;
		}
	});
};
